import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, CATEGORIES, CATEGORY_META } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Search, Clock, Gavel, MapPin, Ticket, Play, Film, LayoutGrid, Navigation, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { VideoEmbed } from "@/components/VideoEmbed";
import { useGeolocation } from "@/hooks/useGeolocation";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Money } from "@/components/Money";
import { CityAutocomplete } from "@/components/CityAutocomplete";

const timeLeft = (iso, startedLabel = "started") => {
  if (!iso) return null;
  const diff = new Date(iso) - new Date();
  if (diff <= 0) return startedLabel;
  const h = Math.floor(diff / 3.6e6);
  const d = Math.floor(h / 24);
  if (d >= 1) return `${d}d`;
  const m = Math.floor((diff % 3.6e6) / 6e4);
  return h >= 1 ? `${h}h ${m}m` : `${m}m`;
};

const EventCard = ({ ev }) => {
  const meta = CATEGORY_META[ev.category] || CATEGORY_META.other;
  const isAuction = ev.ticket_type === "auction";
  const isFree = ev.ticket_type === "free";
  const { t } = useTranslation();
  const priceLabel = isFree ? (
    t("event.free")
  ) : (
    <Money usd={isAuction ? ev.current_bid || ev.starting_bid || 0 : ev.price || 0} decimals={0} inline approxClassName="text-[11px] text-slate-300 font-normal" testid={`event-card-price-${ev.id}`} />
  );

  const distanceLabel = ev.distance_km != null
    ? ev.distance_km < 1
      ? `${Math.round(ev.distance_km * 1000)}m`
      : ev.distance_km < 100
      ? `${ev.distance_km.toFixed(1)}km`
      : `${Math.round(ev.distance_km)}km`
    : null;

  return (
    <Link
      to={`/event/${ev.id}`}
      data-testid={`event-card-${ev.id}`}
      className="group block rounded-2xl overflow-hidden bg-[#131A26] border border-white/10 hover:border-white/25 hover:-translate-y-0.5 transition-all"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={ev.image_url || `https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=800&q=70`}
          alt={ev.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/40 to-transparent" />
        <div className="absolute top-3 left-3 flex gap-2">
          <span className={`overline px-2.5 py-1 rounded-full bg-${meta.color}-500/20 text-${meta.color}-300 border border-${meta.color}-500/30`}>
            {t(`categories.${ev.category}`, meta.label)}
          </span>
          {isAuction && (
            <span className="inline-flex items-center gap-1 overline px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 pulse-glow">
              <Gavel size={11} /> {t("event.auction_badge")}
            </span>
          )}
          {ev.video_url && (
            <span className="inline-flex items-center gap-1 overline px-2.5 py-1 rounded-full bg-black/60 text-white border border-white/25">
              <Play size={11} fill="white" strokeWidth={0} /> REEL
            </span>
          )}
        </div>
        <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/15 font-mono text-sm font-bold">
          {priceLabel}
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-display font-bold text-lg leading-snug line-clamp-2 mb-2">{ev.title}</h3>
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span className="inline-flex items-center gap-1">
            <MapPin size={12} /> {ev.city}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock size={12} /> {timeLeft(ev.starts_at, t("feed.started")) || "TBA"}
          </span>
        </div>
      </div>
    </Link>
  );
};

export default function Feed() {
  const { t } = useTranslation();
  const [cityQuery, setCityQuery] = useState("");
  const [cityPick, setCityPick] = useState(null);
  const [events, setEvents] = useState([]);
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid"); // grid | reels
  const [nearMe, setNearMe] = useState(false);
  const [radiusKm, setRadiusKm] = useState(50);
  const geo = useGeolocation();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (category !== "all") params.category = category;
      if (q) params.q = q;
      const center = nearMe && geo.loc ? geo.loc : cityPick;
      if (center) {
        params.lat = center.lat;
        params.lng = center.lng;
        params.radius_km = radiusKm;
        params.sort = "distance";
      }
      const { data } = await api.get("/events", { params });
      setEvents(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [category, nearMe, radiusKm, geo.loc?.lat, geo.loc?.lng, cityPick?.lat, cityPick?.lng]);

  const toggleNearMe = async () => {
    if (nearMe) {
      setNearMe(false);
      return;
    }
    try {
      if (!geo.loc) {
        await geo.requestLocation();
      }
      setNearMe(true);
      setCityPick(null);
      setCityQuery("");
    } catch (err) {
      toast.error(err.message || "Couldn't get your location");
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] pb-28">
      <TopBar />

      <main className="max-w-6xl mx-auto pt-20 px-4">
        <div className="mb-4">
          <div className="overline text-rose-400 mb-1">{t("feed.eyebrow")}</div>
          <div className="flex items-end justify-between gap-3">
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight">
              {cityPick ? `${cityPick.city}.` : t("feed.title")}
            </h1>
            <div className="flex items-center gap-1 p-1 rounded-full bg-[#131A26] border border-white/10 shrink-0" data-testid="feed-view-toggle">
              <button
                onClick={() => setView("grid")}
                data-testid="feed-view-grid"
                className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1 transition-colors ${
                  view === "grid" ? "bg-rose-500 text-white" : "text-slate-400"
                }`}
              >
                <LayoutGrid size={12} /> {t("feed.grid")}
              </button>
              <button
                onClick={() => setView("reels")}
                data-testid="feed-view-reels"
                className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1 transition-colors ${
                  view === "reels" ? "bg-rose-500 text-white" : "text-slate-400"
                }`}
              >
                <Film size={12} /> {t("feed.reels")}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <CityAutocomplete
          testid="feed-city-picker"
          value={cityQuery}
          onChange={setCityQuery}
          onSelect={(r) => { setCityPick(r); setCityQuery(`${r.city}, ${r.country}`); setNearMe(false); }}
          onClear={() => { setCityPick(null); setCityQuery(""); }}
        />
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            data-testid="feed-search"
            placeholder={t("feed.search_placeholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="pl-9 h-11 bg-[#131A26] border-white/10"
          />
        </div>
        </div>

        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3 mb-4 -mx-4 px-4 items-center">
          <button
            onClick={toggleNearMe}
            disabled={geo.loading}
            data-testid="feed-near-me"
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-colors inline-flex items-center gap-1.5 ${
              nearMe
                ? "bg-emerald-500 border-emerald-500 text-white"
                : "bg-transparent border-white/15 text-slate-300 hover:text-white hover:border-white/30"
            }`}
          >
            {geo.loading ? <Loader2 size={14} className="animate-spin" /> : <Navigation size={14} />}
            {geo.loading ? t("feed.locating") : nearMe ? `${radiusKm}km` : t("feed.near_me")}
          </button>
          {(nearMe || cityPick) && (
            <select
              value={radiusKm}
              onChange={(e) => setRadiusKm(parseInt(e.target.value))}
              data-testid="feed-radius-select"
              className="shrink-0 px-3 py-2 rounded-full bg-[#131A26] border border-white/10 text-xs font-mono"
            >
              <option value={5}>5 km</option>
              <option value={10}>10 km</option>
              <option value={25}>25 km</option>
              <option value={50}>50 km</option>
              <option value={100}>100 km</option>
              <option value={500}>500 km</option>
              <option value={2000}>{t("feed.nationwide")}</option>
            </select>
          )}
          <div className="w-px h-6 bg-white/10 shrink-0" />
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              data-testid={`cat-${c.key}`}
              onClick={() => setCategory(c.key)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                category === c.key
                  ? "bg-rose-500 border-rose-500 text-white"
                  : "bg-transparent border-white/10 text-slate-400 hover:text-white hover:border-white/25"
              }`}
            >
              {t(`categories.${c.key}`, c.label)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="aspect-[16/12] bg-[#131A26] rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : events.length === 0 ? (
          <div data-testid="feed-empty" className="text-center py-24">
            <Ticket size={48} className="mx-auto text-slate-600 mb-4" />
            <h3 className="font-display font-bold text-xl mb-2">{t("feed.empty_title")}</h3>
            <p className="text-slate-400 text-sm mb-6">{t("feed.empty_sub")}</p>
            <Link to="/post" data-testid="feed-post-btn" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-rose-500 hover:bg-rose-400 text-white font-bold text-sm">
              {t("feed.post_btn")}
            </Link>
          </div>
        ) : view === "reels" ? (
          (() => {
            const reels = events.filter((e) => e.video_url);
            if (reels.length === 0) {
              return (
                <div className="text-center py-24" data-testid="feed-reels-empty">
                  <Film size={48} className="mx-auto text-slate-600 mb-4" />
                  <h3 className="font-display font-bold text-xl mb-2">{t("feed.no_reels")}</h3>
                  <p className="text-slate-400 text-sm mb-6">{t("feed.no_reels_sub")}</p>
                  <Link to="/post" data-testid="feed-post-reel" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-rose-500 hover:bg-rose-400 text-white font-bold text-sm">
                    {t("feed.post_reel")}
                  </Link>
                </div>
              );
            }
            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="feed-reels">
                {reels.map((ev) => {
                  const m = CATEGORY_META[ev.category] || CATEGORY_META.other;
                  return (
                    <div key={ev.id} data-testid={`reel-${ev.id}`} className="rounded-2xl overflow-hidden bg-[#131A26] border border-white/10">
                      <VideoEmbed url={ev.video_url} />
                      <Link to={`/event/${ev.id}`} className="block p-3 hover:bg-white/5">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`overline text-${m.color}-400 text-[10px]`}>{m.label}</span>
                          <span className="text-slate-500 text-[10px]">·</span>
                          <span className="text-xs text-slate-400 inline-flex items-center gap-1"><MapPin size={10} /> {ev.city}</span>
                        </div>
                        <div className="font-display font-bold text-sm leading-tight line-clamp-2">{ev.title}</div>
                      </Link>
                    </div>
                  );
                })}
              </div>
            );
          })()
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map((ev) => (
              <EventCard key={ev.id} ev={ev} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
